import { AdvancedContextualSegments } from '@openx/components-targeting/Targeting/AdvancedTargeting/AdvancedContextualSegments';
import { AdvancedTargeting } from '@openx/components-targeting/Targeting/AdvancedTargeting/AdvancedTargeting';
import { AppInventory } from '@openx/components-targeting/Targeting/AppInventory/AppInventory';
import { Audience } from '@openx/components-targeting/Targeting/Audience/Audience';
import { Category } from '@openx/components-targeting/Targeting/Category/Category';
import { ContentObject } from '@openx/components-targeting/Targeting/ContentObject/ContentObject';
import { ContextualSegmentsTargeting } from '@openx/components-targeting/Targeting/ContextualSegments/ContextualSegmentsTargeting';
import { useStandaloneContextualSegmentsEnabled } from '@openx/components-targeting/Targeting/ContextualSegments/featureFlags';
import { Domains } from '@openx/components-targeting/Targeting/Domains/Domains';
import { FormatType } from '@openx/components-targeting/Targeting/FormatType/FormatType';
import { Geographic } from '@openx/components-targeting/Targeting/Geographic/Geographic';
import { InventoryAndContent } from '@openx/components-targeting/Targeting/InventoryAndContent/InventoryAndContent';
import { Metacategories } from '@openx/components-targeting/Targeting/Metacategories/Metacategories';
import { Retargeting } from '@openx/components-targeting/Targeting/Retargeting/Retargeting';
import { TechnologyAndDevices } from '@openx/components-targeting/Targeting/TechnologyAndDevices/TechnologyAndDevices';
import { Video } from '@openx/components-targeting/Targeting/Video/Video';
import { Viewability } from '@openx/components-targeting/Targeting/Viewability/Viewability';
import { ViewThroughRate } from '@openx/components-targeting/Targeting/ViewThroughRate/ViewThroughRate';
import { useTargetingContext } from '@openx/components-targeting/utils/context/TargetingContext';
import { type TargetingConfig, TargetingFields } from '@openx/types/targeting/targeting';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CustomTargetingWithContextualSegmentsField } from '../../components/PackageForm/sections/CustomTargetingWithContextualSegmentsField';

export const useTargeting = (): TargetingConfig[] => {
  const { t } = useTranslation();
  const { useContentObjectOptionsConfig, isOa, useRetargetingSegmentsOptionsFetch } = useTargetingContext();
  const isStandaloneContextualSegmentsEnabled = useStandaloneContextualSegmentsEnabled();

  const conditional = useCallback((element: TargetingConfig, condition: boolean) => (condition ? [element] : []), []);

  return useMemo(() => {
    return [
      {
        TargetingComponent: Audience,
        field: TargetingFields.AUDIENCE,
        name: t('Audience'),
      },
      ...conditional(
        {
          TargetingComponent: ContextualSegmentsTargeting,
          field: TargetingFields.CONTEXTUAL_SEGMENTS,
          name: t('Contextual Segments'),
        },
        !!isOa && isStandaloneContextualSegmentsEnabled
      ),
      {
        TargetingComponent: InventoryAndContent,
        field: TargetingFields.INVENTORY_AND_CONTENT,
        name: t('Inventory'),
      },
      {
        TargetingComponent: Domains,
        field: TargetingFields.URL_TARGETING,
        name: t('Domains'),
      },
      {
        TargetingComponent: AppInventory,
        field: TargetingFields.APP_INVENTORY,
        name: t('App Inventory'),
      },
      {
        TargetingComponent: Geographic,
        field: TargetingFields.GEOGRAPHIC,
        name: t('Current Location'),
      },
      {
        TargetingComponent: TechnologyAndDevices,
        field: TargetingFields.TECHNOLOGY_AND_DEVICES,
        name: t('Environment'),
      },
      {
        TargetingComponent: Category,
        field: TargetingFields.CATEGORY,
        name: t('IAB Categories'),
      },
      {
        TargetingComponent: Metacategories,
        field: TargetingFields.METACATEGORY,
        name: t('OpenX Categories and Keywords'),
      },
      {
        TargetingComponent: FormatType,
        field: TargetingFields.FORMAT_TYPE,
        name: t('Distribution Channel'),
      },
      {
        TargetingComponent: Viewability,
        field: TargetingFields.VIEWABILITY,
        name: t('Viewability'),
      },
      {
        TargetingComponent: Video,
        field: TargetingFields.VIDEO,
        name: t('Video Attributes'),
      },
      {
        TargetingComponent: ViewThroughRate,
        field: TargetingFields.VIEW_THROUGH_RATE,
        name: t('View Through Rate'),
      },
      ...conditional(
        {
          TargetingComponent: Retargeting,
          field: TargetingFields.RETARGETING,
          name: t('Retargeting'),
        },
        !!useRetargetingSegmentsOptionsFetch
      ),
      ...conditional(
        {
          TargetingComponent: ContentObject,
          field: TargetingFields.CONTENT_OBJECT,
          name: t('CTV Content Object'),
        },
        !!useContentObjectOptionsConfig
      ),
      // With the standalone flag, Contextual Segments get their own card: V3 in OA (placed after Audience above),
      // V1 elsewhere. Without it, they are a section of the Custom Targeting card (V2 in OA, V1 elsewhere).
      ...(isStandaloneContextualSegmentsEnabled
        ? [
            ...conditional(
              {
                TargetingComponent: AdvancedContextualSegments,
                field: TargetingFields.CONTEXTUAL_SEGMENTS,
                name: t('Contextual Segments'),
              },
              !isOa
            ),
            {
              TargetingComponent: AdvancedTargeting,
              field: TargetingFields.CUSTOM_VARIABLES,
              name: t('Custom Targeting'),
            },
          ]
        : [
            {
              TargetingComponent: CustomTargetingWithContextualSegmentsField,
              field: TargetingFields.CUSTOM_VARIABLES,
              name: t('Custom Targeting'),
            },
          ]),
    ];
  }, [
    t,
    conditional,
    useContentObjectOptionsConfig,
    useRetargetingSegmentsOptionsFetch,
    isOa,
    isStandaloneContextualSegmentsEnabled,
  ]);
};
